// V2 visual-slot equivalent of v1's DemandTimelineDisplay.
//
// V1's component renders the aggregated customer-order demand for an item
// (overdue/today/tomorrow/this-week buckets). V2 has one WO per
// CustomerOrderLine, so there is no aggregated cross-order demand to display
// — we render a single-line summary of the WO's own demand (customer order +
// quantity + due date) so the visual slot is preserved and the operator
// understands the source of the WO.

import React from 'react';
import { Box, Typography, Chip, Alert } from '@mui/material';
import { Timeline, Assignment, Warning } from '@mui/icons-material';

function daysUntil(due) {
  if (!due) return null;
  const now = new Date();
  const d = new Date(due);
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

function urgencyChip(days) {
  if (days == null) return null;
  if (days < 0) return { label: `${Math.abs(days)}d OVERDUE`, color: 'error', icon: <Warning /> };
  if (days === 0) return { label: 'TODAY', color: 'error' };
  if (days === 1) return { label: 'TOMORROW', color: 'warning' };
  if (days <= 7) return { label: 'THIS WEEK', color: 'info' };
  return { label: 'FUTURE', color: 'default' };
}

export default function DemandTimelineDisplayV2({ step }) {
  if (!step) return null;

  const ordered = parseFloat(step.line_quantity_ordered || 0);
  const shipped = parseFloat(step.line_quantity_shipped || 0);
  const remaining = Math.max(0, ordered - shipped);
  const days = daysUntil(step.due_date);
  const urgency = urgencyChip(days);

  return (
    <Box data-testid="demand-timeline-v2">
      <Typography
        variant="subtitle2"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Timeline />
        Demand Source
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {urgency && (
          <Chip label={urgency.label} color={urgency.color} size="small" icon={urgency.icon} />
        )}
        <Chip
          icon={<Assignment />}
          label={`${step.customer_order_number || '-'} · L${step.line_number}`}
          variant="outlined"
          size="small"
          color="primary"
        />
        <Chip
          label={`Total ${remaining || step.quantity_to_produce} units`}
          variant="outlined"
          size="small"
        />
      </Box>
      <Alert severity="info">
        <Typography variant="body2" component="div">
          <strong>Customer:</strong> {step.customer_name || '—'}
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Ordered:</strong> {Math.round(ordered)} ·{' '}
          <strong>Shipped:</strong> {Math.round(shipped)} ·{' '}
          <strong>Outstanding:</strong> {Math.round(remaining)}
        </Typography>
        {step.due_date && (
          <Typography variant="body2" component="div">
            <strong>Due:</strong> {new Date(step.due_date).toLocaleDateString()}
            {days != null && (
              <>
                {' '}
                {days < 0
                  ? `(${Math.abs(days)} days overdue)`
                  : days === 0
                    ? '(due today!)'
                    : `(${days} days left)`}
              </>
            )}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Note: v2 work orders are scoped to a single customer-order line, so this
          panel shows the line's demand rather than aggregated cross-order demand.
        </Typography>
      </Alert>
    </Box>
  );
}
